import React, { useMemo, useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    Grid,
    FormControlLabel,
    Checkbox,
    Paper,
    useTheme,
    DialogContent,
    DialogActions,
    DialogTitle,
    Tabs,
    Tab,
} from '@mui/material';
import {
    NivelAcesso,
    SubNivelAcesso,
} from '../../../services/api/users/UserServices';

export type PermissaoTipo = 'add' | 'edit' | 'remove' | 'view';
export type PermissaoAtualizacao = PermissaoTipo | 'toggle';

export const NivelAcessoLabels: Record<keyof NivelAcesso, string> = {
    dashboard: 'Dashboard',
    informacoes: 'Informações',
    documentacao_tecnica: 'Documentação Técnica',
    registros: 'Registros',
    acompanhamentos: 'Acompanhamentos',
    revisao: 'Revisão',
    PAE: 'PAE',
    notificacao: 'Notificações',
};

export const SubNivelAcessoLabels: {
    [key in keyof NivelAcesso]?: { [K in keyof SubNivelAcesso]?: string };
} = {
    dashboard: {
        dashboardGeral: 'Dashboard de informação',
    },
    informacoes: {
        central: 'Central de comunicação',
        identificacao: 'Identificação do empreendedor',
        caracterizacao: 'Caracterização de barragem',
        organograma: 'Organograma',
    },
    documentacao_tecnica: {
        caracteristicas: 'Características técnicas do projeto e da construção',
        arquivos: 'Arquivos da barragem',
    },
    registros: {
        RISR: 'RISR',
        RISROT: 'RISROT',
        RISE: 'RISE',
        operacoes: 'Registros de operação',
        monitoramento: 'Registros de monitoramento',
    },
    acompanhamentos: {
        comunicacao: 'Central de comunicação',
        visualizacao: 'Visualização e instrumentação',
        inspecoes: 'Inspeções de segurança',
        anomalias: 'Acompanhamento de anomalias',
        plano: 'Plano de ação',
    },
    revisao: {
        RPSB: 'RPSB',
    },
    PAE: {
        PAE: 'Plano de Ação de Emergência',
        conformidades: 'Avaliação de conformidade e operacionalidade',
        treinamento: 'Treinamento interno semestral',
        seminario: 'Seminário orientativo anual',
        simulado: 'Simulado de situação de emergência',
        registro: 'Registro de situações de emergência',
    },
    notificacao: {
        leitura: 'Notificações de leitura de instrumentos',
        novo_risrot: 'Notificações de novo RISROT',
        nova_anomalia: 'Notificações de novo cadastro de anomalia',
        inicio_atividade: 'Notifica o início de uma atividade',
        novo_plano_acao: 'Notificações de novo cadastro de plano de ação',
        status_plano: 'Notifica a mudança de status de um plano',
        status_tarefa: 'Notifica a mudança de status de uma tarefa',
        nova_tarefa: 'Notificações de nova tarefa',
        novo_topico: 'Notificações de novo tópico',
    },
};

interface NivelAcessoModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentNivelAcesso: NivelAcesso;
    onUpdateNivelAcesso: (
        categoria: keyof NivelAcesso,
        subCategoria: keyof SubNivelAcesso,
        permission: PermissaoAtualizacao,
        isChecked: boolean
    ) => void;
}

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    color: 'text.primary',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
};

export const NivelAcessoModal: React.FC<NivelAcessoModalProps> = ({
    isOpen,
    onClose,
    currentNivelAcesso,
    onUpdateNivelAcesso,
}) => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState<keyof NivelAcesso>('dashboard');

    const permissionTypes: PermissaoTipo[] = ['add', 'edit', 'remove', 'view'];
    const dashboardPermissionTypes: PermissaoTipo[] = ['view'];

    const permissionLabels: Record<PermissaoTipo, string> = {
        add: 'Adicionar',
        edit: 'Editar',
        remove: 'Remover',
        view: 'Visualizar',
    };

    const isNotificationCategory = (categoria: keyof NivelAcesso) =>
        categoria === 'notificacao';

    const isBooleanTrue = (value?: string) => value === 'true';

    const getPermissionsByCategory = (
        categoria: keyof NivelAcesso
    ): PermissaoAtualizacao[] => {
        if (isNotificationCategory(categoria)) return ['toggle'];
        if (categoria === 'dashboard') return dashboardPermissionTypes;
        return permissionTypes;
    };

    const isAllSelected = useMemo(() => {
        const activeCategoria = activeTab;
        const subCategorias = SubNivelAcessoLabels[activeCategoria];
        if (!subCategorias) return false;

        return Object.keys(subCategorias).every(subCategoriaKeyString => {
            const subCategoriaKey = subCategoriaKeyString as keyof SubNivelAcesso;
            const currentValue =
                currentNivelAcesso?.[activeCategoria]?.[subCategoriaKey];

            if (isNotificationCategory(activeCategoria)) {
                return isBooleanTrue(currentValue);
            }

            const permissions = currentValue
                ? currentValue.split(',').map(p => p.trim())
                : [];

            const permissionsToCheck = getPermissionsByCategory(
                activeCategoria
            ) as PermissaoTipo[];

            return permissionsToCheck.every(p => permissions.includes(p));
        });
    }, [currentNivelAcesso, activeTab]);

    const handleToggleAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        const categoriaKey = activeTab;
        const subCategorias = SubNivelAcessoLabels[categoriaKey];

        if (!subCategorias) return;

        Object.keys(subCategorias).forEach(subCategoriaKeyString => {
            const subCategoriaKey = subCategoriaKeyString as keyof SubNivelAcesso;

            if (isNotificationCategory(categoriaKey)) {
                onUpdateNivelAcesso(categoriaKey, subCategoriaKey, 'toggle', isChecked);
                return;
            }

            const permissionsToToggle = getPermissionsByCategory(
                categoriaKey
            ) as PermissaoTipo[];

            permissionsToToggle.forEach(permission => {
                onUpdateNivelAcesso(categoriaKey, subCategoriaKey, permission, isChecked);
            });
        });
    };

    const renderPermissionCheckboxes = (
        categoriaKey: keyof NivelAcesso,
        subCategoriaKey: keyof SubNivelAcesso,
        currentPermissionsString: string | undefined
    ) => {
        if (isNotificationCategory(categoriaKey)) {
            return (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={isBooleanTrue(currentPermissionsString)}
                                onChange={e =>
                                    onUpdateNivelAcesso(
                                        categoriaKey,
                                        subCategoriaKey,
                                        'toggle',
                                        e.target.checked
                                    )
                                }
                                sx={{ color: theme.palette.primary.main }}
                            />
                        }
                        label="Ativar"
                        sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                    />
                </Box>
            );
        }

        const permissions = currentPermissionsString
            ? currentPermissionsString.split(',').map(p => p.trim())
            : [];

        const permissionsToRender =
            categoriaKey === 'dashboard' ? dashboardPermissionTypes : permissionTypes;

        return (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
                {permissionsToRender.map(permission => (
                    <FormControlLabel
                        key={permission}
                        control={
                            <Checkbox
                                size="small"
                                checked={permissions.includes(permission)}
                                onChange={e =>
                                    onUpdateNivelAcesso(
                                        categoriaKey,
                                        subCategoriaKey,
                                        permission,
                                        e.target.checked
                                    )
                                }
                                sx={{ color: theme.palette.primary.main }}
                            />
                        }
                        label={permissionLabels[permission]}
                        sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                    />
                ))}
            </Box>
        );
    };

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            aria-labelledby="nivel-acesso-modal-title"
            aria-describedby="nivel-acesso-modal-description"
        >
            <Box sx={modalStyle}>
                <DialogTitle sx={{ pb: 1, pt: 2 }}>
                    <Typography variant="h6" id="nivel-acesso-modal-title">
                        Configurar Níveis de Acesso
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, newTab) => setActiveTab(newTab)}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="abas de categorias"
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        {Object.keys(NivelAcessoLabels).map(categoriaKey => (
                            <Tab
                                key={categoriaKey}
                                label={NivelAcessoLabels[categoriaKey as keyof NivelAcesso]}
                                value={categoriaKey}
                                sx={{ textTransform: 'none' }}
                            />
                        ))}
                    </Tabs>

                    <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
                        <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <FormControlLabel
                                control={<Checkbox checked={isAllSelected} onChange={handleToggleAll} />}
                                label={`Marcar/Desmarcar Tudo em "${NivelAcessoLabels[activeTab]}"`}
                            />
                        </Box>

                        {Object.keys(NivelAcessoLabels).map(categoriaKeyString => {
                            const categoriaKey = categoriaKeyString as keyof NivelAcesso;

                            if (categoriaKey !== activeTab) {
                                return null;
                            }

                            const subCategorias = SubNivelAcessoLabels[categoriaKey];
                            const currentCategoriaData = currentNivelAcesso?.[categoriaKey];

                            if (!subCategorias || Object.keys(subCategorias).length === 0) {
                                return null;
                            }

                            return (
                                <Grid container spacing={2} key={categoriaKey}>
                                    {Object.keys(subCategorias).map(subCategoriaKeyString => {
                                        const subCategoriaKey =
                                            subCategoriaKeyString as keyof SubNivelAcesso;

                                        const subCategoriaLabel = (
                                            subCategorias as Record<keyof SubNivelAcesso, string>
                                        )[subCategoriaKey];

                                        const currentPermissions =
                                            currentCategoriaData?.[subCategoriaKey];

                                        return (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                md={4}
                                                key={`${categoriaKey}-${subCategoriaKey}`}
                                            >
                                                <Paper
                                                    elevation={2}
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 1,
                                                        bgcolor: theme.palette.background.default,
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                        sx={{ mb: 0.5, color: theme.palette.text.secondary }}
                                                    >
                                                        {subCategoriaLabel}:
                                                    </Typography>

                                                    {renderPermissionCheckboxes(
                                                        categoriaKey,
                                                        subCategoriaKey,
                                                        currentPermissions
                                                    )}
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            );
                        })}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ pt: 2, pr: 0 }}>
                    <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.common.white,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Box>
        </Modal>
    );
};